import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postAddCertificatesOrganizations } from '~apis/bank/postAddCertificateOrganizations';
import postCertificates from '~apis/certificates/postCertificates';
import { BankAccountCashFlowsQueryKey } from '~hooks/queries/bank-accounts/cashFlows/query';
import { BankAccountsQueryKey } from '~hooks/queries/bank-accounts/query';
import { OrganizationsQueryKey } from '~hooks/queries/organizations/query';
import { IntegratedScrapeQueryStatusKeys } from '~hooks/queries/scraping/useGetIntegratedScrapeStatus';
import { useCompanyId } from '~hooks/useCompanyId';
import { DemoQueryKey } from '~hooks/useDemoCompanyId';

import { SettingsQueryKey } from '../setting/query';
import { USE_BUSINESS_REGISTRATION_NUMBERS_QUERY } from '../useBusinessRegistrationNumbers';
import { USE_GET_CERTIFICATES_QUERY } from '../useCertificatesQuery';
import { USE_GET_CORPORATE_CARD_CONNECTIONS_QUERY } from '../useCorporateCardConnections';
import { USE_DATA_QUALIFICATIONS_QUERY } from '../useDataQualificationsQuery';
import { USE_GET_CORPORATE_CARD_LIST_QUERY } from '../useGetCorporateCardList';

export const CertificatesQueryKey = {
  default: () => ['certificates'],
  list: () => [...CertificatesQueryKey.default(), 'list'],
};

export function useSyncCertificatesOrganizations() {
  const queryClient = useQueryClient();
  const { companyId } = useCompanyId();
  return useMutation({
    mutationFn: postAddCertificatesOrganizations,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(BankAccountsQueryKey.list(variables));
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId }));
      queryClient.invalidateQueries(BankAccountCashFlowsQueryKey.default({ companyId }));
    },
  });
}

export function useCreateCertificates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCertificates,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(OrganizationsQueryKey.default());
      queryClient.invalidateQueries(SettingsQueryKey.default());
      queryClient.invalidateQueries(CertificatesQueryKey.list());
      queryClient.invalidateQueries(
        IntegratedScrapeQueryStatusKeys.scrapeStatus(variables.companyId),
      );
      queryClient.invalidateQueries(USE_GET_CORPORATE_CARD_LIST_QUERY.root);
      queryClient.invalidateQueries(USE_GET_CORPORATE_CARD_CONNECTIONS_QUERY.root);
      queryClient.invalidateQueries(USE_GET_CERTIFICATES_QUERY.default(variables.companyId));
      queryClient.invalidateQueries(DemoQueryKey.status());
      if (variables.request.initScrape !== false) {
        queryClient.invalidateQueries(BankAccountsQueryKey.list(variables));
      }
      queryClient.invalidateQueries(USE_DATA_QUALIFICATIONS_QUERY.default(variables.companyId));
      queryClient.invalidateQueries(
        USE_BUSINESS_REGISTRATION_NUMBERS_QUERY.default(variables.companyId),
      );
    },
  });
}
